/* CUSTOM COLLECTION-FILTER-BADGES
Aktualisiert aktive Badge-States aus der URL und rendert Badge-Section sowie
Collection-Grid per AJAX neu, ohne die komplette Seite neu zu laden. */

(function () {
  let customPendingRequestController = null;
  let customPopstateBound = false;

  function customDecodeAndNormalize(value) {
    if (value == null) {
      return null;
    }

    let normalized = String(value).replace(/\+/g, " ");

    try {
      normalized = decodeURIComponent(normalized);
    } catch (error) {
      // CUSTOM: Rohwert beibehalten, wenn decodeURIComponent fehlschlaegt.
    }

    return normalized.trim().toLowerCase();
  }

  function customIsFilterKey(key) {
    return typeof key === "string" && key.startsWith("filter.");
  }

  function customGetBadgeDefinition(anchor) {
    if (!anchor) {
      return null;
    }

    const href = new URL(anchor.getAttribute("href"), window.location.origin);
    const badgeParams = [];

    href.searchParams.forEach((value, key) => {
      if (!customIsFilterKey(key)) {
        return;
      }

      badgeParams.push({
        key,
        value,
        normalizedValue: customDecodeAndNormalize(value),
      });
    });

    if (!badgeParams.length) {
      return null;
    }

    return { href, badgeParams };
  }

  function customParamIsActive(currentParams, badgeParam) {
    const currentValues = currentParams.getAll(badgeParam.key);

    return currentValues.some((value) => customDecodeAndNormalize(value) === badgeParam.normalizedValue);
  }

  function customBadgeIsActive(currentParams, badgeDefinition) {
    if (!badgeDefinition || !badgeDefinition.badgeParams.length) {
      return false;
    }

    return badgeDefinition.badgeParams.every((badgeParam) => customParamIsActive(currentParams, badgeParam));
  }

  function customUpdateBadgeStates(container) {
    const currentUrl = new URL(window.location.href);
    const currentParams = currentUrl.searchParams;

    container.querySelectorAll(".custom-filter-badge").forEach((badge) => {
      const anchor = badge.querySelector("a[href]");
      const badgeDefinition = customGetBadgeDefinition(anchor);
      const isActive = customBadgeIsActive(currentParams, badgeDefinition);

      badge.classList.toggle("badge--active", isActive);

      if (anchor) {
        if (isActive) {
          anchor.setAttribute("aria-current", "page");
        } else {
          anchor.removeAttribute("aria-current");
        }
      }
    });
  }

  function customBuildNextUrl(currentUrl, badgeDefinition, isActive) {
    const nextUrl = new URL(currentUrl.toString());
    const nextParams = new URLSearchParams(currentUrl.search);
    const badgeKeys = [...new Set(badgeDefinition.badgeParams.map((badgeParam) => badgeParam.key))];

    badgeKeys.forEach((key) => {
      nextParams.delete(key);
    });

    if (!isActive) {
      badgeDefinition.badgeParams.forEach((badgeParam) => {
        nextParams.append(badgeParam.key, badgeParam.value);
      });
    }

    nextUrl.search = nextParams.toString() ? `?${nextParams.toString()}` : "";

    return nextUrl;
  }

  function customGetShopifySectionElement(element) {
    return element?.closest(".shopify-section") || null;
  }

  function customExtractSectionId(sectionElement) {
    if (!sectionElement || !sectionElement.id) {
      return null;
    }

    return sectionElement.id.replace(/^shopify-section-/, "") || null;
  }

  function customFindMainCollectionSection() {
    return document.querySelector(".shopify-section--main-collection");
  }

  function customGetTargetSectionIds(container) {
    const badgeSectionId =
      container?.dataset.customBadgeSectionId || customExtractSectionId(customGetShopifySectionElement(container));
    const mainCollectionSectionId = customExtractSectionId(customFindMainCollectionSection());

    if (!badgeSectionId || !mainCollectionSectionId) {
      return null;
    }

    return [badgeSectionId, mainCollectionSectionId];
  }

  function customDispatchLoadingEvent(eventName) {
    document.documentElement.dispatchEvent(new CustomEvent(eventName, { bubbles: true }));
  }

  function customReplaceSectionContent(sectionId, nextDocument) {
    const currentSection = document.getElementById(`shopify-section-${sectionId}`);
    const nextSection = nextDocument.getElementById(`shopify-section-${sectionId}`);

    if (!currentSection || !nextSection) {
      throw new Error(`Missing section markup for ${sectionId}`);
    }

    currentSection.replaceChildren(...document.importNode(nextSection, true).childNodes);
  }

  async function customFetchPageDocument(nextUrl) {
    if (customPendingRequestController) {
      customPendingRequestController.abort();
    }

    customPendingRequestController = new AbortController();

    const response = await fetch(nextUrl.toString(), {
      signal: customPendingRequestController.signal,
    });

    if (!response.ok) {
      throw new Error(`Page request failed with status ${response.status}`);
    }

    const responseText = await response.text();

    return new DOMParser().parseFromString(responseText, "text/html");
  }

  async function customRenderCollectionSections(nextUrl, container) {
    const sectionIds = customGetTargetSectionIds(container);

    if (!sectionIds) {
      throw new Error("Unable to resolve target sections for filter badge update");
    }

    customDispatchLoadingEvent("theme:loading:start");

    try {
      const nextDocument = await customFetchPageDocument(nextUrl);

      sectionIds.forEach((sectionId) => {
        customReplaceSectionContent(sectionId, nextDocument);
      });

      history.replaceState({}, "", nextUrl.toString());
      customBootCollectionFilterBadges();
    } finally {
      customDispatchLoadingEvent("theme:loading:end");
      customPendingRequestController = null;
    }
  }

  function customFallbackToPageNavigation(nextUrl) {
    window.location.assign(nextUrl.toString());
  }

  /* CUSTOM BADGE-INITIALISIERUNG
  Aktiviert die Badge-Navigation einmal pro Container und ersetzt bei Klick
  Badge-Section sowie Collection-Grid per AJAX. */
  function customInitCollectionFilterBadges(container) {
    if (!container || container.dataset.customFilterBadgesInit === "1") {
      return;
    }

    container.dataset.customFilterBadgesInit = "1";
    customUpdateBadgeStates(container);

    container.addEventListener(
      "click",
      async (event) => {
        const link = event.target.closest("a[href]");

        if (!link || !container.contains(link)) {
          return;
        }

        const badgeDefinition = customGetBadgeDefinition(link);

        if (!badgeDefinition) {
          return;
        }

        const currentUrl = new URL(window.location.href);
        const isActive = customBadgeIsActive(currentUrl.searchParams, badgeDefinition);
        const nextUrl = customBuildNextUrl(currentUrl, badgeDefinition, isActive);

        event.preventDefault();

        try {
          await customRenderCollectionSections(nextUrl, container);
        } catch (error) {
          if (error?.name === "AbortError") {
            return;
          }

          customFallbackToPageNavigation(nextUrl);
        }
      },
      { capture: true }
    );
  }

  /* CUSTOM BOOTSTRAP
  Initialisiert die Badges beim ersten Seitenaufbau und nach dynamischen Section-Loads. */
  function customBootCollectionFilterBadges() {
    document.querySelectorAll(".custom-filter-badges-container").forEach(customInitCollectionFilterBadges);

    if (!customPopstateBound) {
      window.addEventListener("popstate", async () => {
        const container = document.querySelector(".custom-filter-badges-container");

        if (!container) {
          return;
        }

        try {
          await customRenderCollectionSections(new URL(window.location.href), container);
        } catch (error) {
          if (error?.name === "AbortError") {
            return;
          }

          window.location.reload();
        }
      });

      customPopstateBound = true;
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", customBootCollectionFilterBadges);
  } else {
    customBootCollectionFilterBadges();
  }

  document.addEventListener("shopify:section:load", customBootCollectionFilterBadges);
})();
